import type { Logger } from "@expert/logging";

import { createContext, useContext, useMemo } from "react";

import type { ShadowComparisonResult } from "./shadow/types";
import type { GaiaWebsocketContext } from "./types";

import { useGaiaWebsocketInternal } from "./hooks/useGaiaWebsocket";

export const GaiaContext = createContext<GaiaWebsocketContext | null>(null);
interface GaiaWebsocketProviderProps {
    url: string;
    application: "expert-workspace" | "expert-assist";
    token: string;
    identity: string;
    logger: Logger;
    children?: React.ReactNode;
    /** Base URL of the Simplr shadow websocket (dark launch). */
    shadowUrl?: string;
    /** When true (and `shadowUrl` set), opens the Simplr shadow connection for comparison. */
    shadowEnabled?: boolean;
    /** Invoked for each GAIA/Simplr message difference (never for equal messages). */
    onShadowComparison?: (result: ShadowComparisonResult) => void;
}

export function GaiaWebsocketProvider({
    url,
    application,
    token,
    identity,
    children,
    logger,
    shadowUrl,
    shadowEnabled,
    onShadowComparison,
}: GaiaWebsocketProviderProps) {
    const shadow = useMemo(
        () => ({ url: shadowUrl, enabled: shadowEnabled, onComparison: onShadowComparison }),
        [shadowUrl, shadowEnabled, onShadowComparison],
    );
    const webSocketObject = useGaiaWebsocketInternal({
        baseUrl: url,
        application,
        token,
        identity,
        logger,
        shadow,
    });
    return <GaiaContext.Provider value={webSocketObject}>{children}</GaiaContext.Provider>;
}

export function useGaiaWebsocket() {
    const context = useContext(GaiaContext);
    return context;
}
